import { compact, findKey, isEmpty, isNil, isNumber, isUndefined } from 'lodash';
import {
  CoreListingLineItem,
  Currency,
  ListingCondition,
  Listing as PGListingType,
} from '../../types/apollo/generated/types.generated';
import { Filter, FilterId, GenericFilterOption } from '../../types/filters/filters';
import {
  ListingAttributes,
  ListingItemType,
  ListingWithAuthorAndImages,
  Listing as STListingType,
} from '../../types/sharetribe/listing';
import { createResourceLocatorString } from '../routes';
import { findOptionsForSelectFilter } from '../search';
import { LISTING_STATE_DRAFT, LISTING_STATE_PENDING_APPROVAL } from '../types';
import {
  createSlug,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  ListingPageParamType,
} from '../urlHelpers';
import { Product, ShopifyProductVariant } from '../../types/shopify/product';
import { ITradeInCreditPayoutOption } from '../../types/contentful/types.generated';
import { FulfillmentMethod } from '../../types/shopConfig/shopConfigV2';
import { getFulfillmentMethod } from '../helpers';
import { Uuid } from '../../types/sharetribe/uuid';
import {
  LISTING_CONDITION_TO_ST_CONDITION,
  LISTING_FIND_ITEM_METHOD_TO_ST_FIND_ITEM_METHOD,
  ST_CONDITION_TO_LISTING_CONDITION,
  ST_FIND_ITEM_METHOD_TO_LISTING_FIND_ITEM_METHOD,
  ST_LISTING_STATE_TO_LISTING_STATUS,
} from '../../types/models/listing';
import * as log from '../log';
import { Money } from '../../types/sharetribe/money';
import { unitDivisor } from '../currency';

// Find item methods that represent user-facing entrypoints in the listing flow.
export enum ListingFlowFindItemMethod {
  Search = 'search',
  EmailAccount = 'email_account',
  OrderNumber = 'order_number',
  CannotFind = 'cannot_find',
  ListAnyBrand = 'list_any_brand',
  // TODO (TREET-5308) Update all_products to be fully considered a ListingFlowFindItemMethod.
  // Refactor shouldShowOtherItemsInSearch config field and update listing flow config
  // field in contentful.
  AllProducts = 'all_products',
}

export enum PhotoTips {
  NeutralBackground = 'NEUTRAL_BACKGROUND',
  WhiteBackground = 'WHITE_BACKGROUND',
  HangIt = 'HANG_IT',
  HangItOrLayItFlat = 'HANG_IT_OR_LAY_FLAT',
  Clean = 'CLEAN',
  CleanAndIroned = 'CLEAN_AND_IRONED',
  GoodLighting = 'GOOD_LIGHTING',
  CaptureQuirks = 'CAPTURE_QUIRKS',
}

export enum OriginalPriceSource {
  CompareAtPrice = 'Compare At Price',
  RegularPrice = 'Regular Price',
}

export const PHOTO_TIPS_TO_LABEL = {
  [PhotoTips.NeutralBackground]: 'Neutral Background',
  [PhotoTips.WhiteBackground]: 'Select a White Background',
  [PhotoTips.HangIt]: 'Hang It',
  [PhotoTips.HangItOrLayItFlat]: 'Hang It or Lay Flat',
  [PhotoTips.Clean]: 'Clean',
  [PhotoTips.CleanAndIroned]: 'Clean & Ironed',
  [PhotoTips.GoodLighting]: 'Good Lighting',
  [PhotoTips.CaptureQuirks]: 'Capture Quirks',
};

// Get the product description for a listing
export const getProductDescription = (
  listingAttributes: ListingAttributes,
  showProductDescription: boolean,
  treetId: string
) => {
  const { shopifyProduct, bigCommerceProduct, productHtml } = listingAttributes.publicData;

  if (!showProductDescription) {
    return null;
  }

  if (shopifyProduct) {
    const { description, descriptionHtml } = shopifyProduct || {};

    if (treetId === 'anian') {
      return descriptionHtml.split('<!--split-->')[0];
    }

    if (treetId === 'kurufootwear') {
      return descriptionHtml.replace(/📦 Free Shipping.*Expert Customer Service/g, '');
    }

    return descriptionHtml || description || '';
  }

  if (bigCommerceProduct) {
    const { description } = bigCommerceProduct || {};
    if (treetId === 'cariloha') {
      return `<div style="white-space:normal;">${
        description.replaceAll('display: none', '') || ''
      }</div>`;
    }
    return description || '';
  }

  if (productHtml) {
    return productHtml;
  }

  return null;
};

// Get the label for a condition
export const getConditionLabel = (conditionKey: string | undefined, filters: Filter[]) => {
  if (!conditionKey) return null;

  const conditionOptions = findOptionsForSelectFilter(FilterId.Condition, filters);
  const condition = conditionOptions.find((c: GenericFilterOption) => c.key === conditionKey);

  return condition ? condition.label : condition;
};

export const createListingURLHelper = (routes: any, title: string, id: string, state: string) => {
  const slug = createSlug(title);
  const isPendingApproval = state.toLowerCase() === LISTING_STATE_PENDING_APPROVAL.toLowerCase();
  const isDraft = state.toLowerCase() === LISTING_STATE_DRAFT.toLowerCase();

  let variant;
  if (isPendingApproval) {
    variant = LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  }

  const linkProps = isPendingApproval
    ? {
        name: 'ListingPageVariant',
        params: { id, slug, variant },
      }
    : isDraft
    ? {
        name: 'EditListingPage',
        params: { id, slug, type: ListingPageParamType.Draft, tab: 'shipping' },
      }
    : {
        name: 'ListingPage',
        params: { id, slug },
      };

  return createResourceLocatorString(linkProps.name, routes, linkProps.params, {});
};

export const createListingURLFromListing = (routes: any, listing: STListingType) => {
  const id = listing.id.uuid;

  return createListingURLHelper(routes, listing.attributes.title, id, listing.attributes.state);
};

// Keep in sync with server/utils/listings.ts
const getPayoutValuesForListing = (
  listing: STListingType,
  listingLineItem?: CoreListingLineItem | undefined
): { cash: number; credit: number } => {
  let cash = 0;
  let credit = 0;
  const payoutInfo = listing?.attributes?.publicData?.payoutInfo;
  // Use listing line item price if available (for purchased listings).
  const price = listingLineItem?.price || listing?.attributes?.price?.amount;
  const isTradeInItem =
    listing?.attributes?.publicData?.listingItemType === ListingItemType.TradeIn;
  // Trade-in listings can be $0
  if (isTradeInItem || (payoutInfo && price)) {
    const { cashPayoutPercentage, creditPayoutPercentage, creditPayoutFixedAmount } = payoutInfo;

    if (isNumber(cashPayoutPercentage) && cashPayoutPercentage >= 0) {
      cash = cashPayoutPercentage * price;
    }

    if (isNumber(creditPayoutFixedAmount) && creditPayoutFixedAmount >= 0) {
      credit = creditPayoutFixedAmount;
    } else {
      credit = creditPayoutPercentage * price;
    }
  }

  return { cash, credit };
};

// Keep in sync with server/utils/listings.ts
export const getTotalPayoutValueForListings = (
  listings: STListingType[],
  listingLineItems?: CoreListingLineItem[] | undefined
): { cash: number; credit: number } => {
  if (isEmpty(listings)) return { cash: 0, credit: 0 };

  const { cash, credit } = listings.reduce(
    (acc, listing) => {
      const listingLineItem = listingLineItems?.find(
        (lineItem) => lineItem?.sharetribeListingId === listing.id.uuid
      );
      const listingTotal = getPayoutValuesForListing(listing, listingLineItem);
      acc.cash += listingTotal.cash;
      acc.credit += listingTotal.credit;
      return acc;
    },
    { cash: 0, credit: 0 }
  );

  const maxCreditPerBundle =
    listings[0]?.attributes?.publicData?.payoutInfo?.tradeInCreditPayoutBundleAmount;

  const isTradeInBundle = listings.every(
    (listing) => listing.attributes?.publicData?.listingItemType === ListingItemType.TradeIn
  );

  // For trade-in bundles with a flat amount of credit per bundle,
  // credit issued can be either $0 if all
  // items are disputed, or the full amount.
  const maybeAdjustedCredit =
    maxCreditPerBundle && isTradeInBundle ? Math.min(credit, maxCreditPerBundle) : credit;

  return { cash: Math.round(cash), credit: Math.round(maybeAdjustedCredit) };
};

const getLegacyOriginalShopifyProductPrice = (
  originalPriceSources: OriginalPriceSource[],
  shopifyProduct?: Pick<Product, 'priceRange'>,
  shopifyProductVariant?: Pick<ShopifyProductVariant, 'compareAtPrice' | 'price'> | null
): number | undefined => {
  // Product variant is formatted like "10.00", price on shopify product is
  // formatted like "1000"
  const compareAtPrice = Number(shopifyProductVariant?.compareAtPrice || null);
  const regularPrice = Number(shopifyProductVariant?.price || null);
  const maxPrice = Number(shopifyProduct?.priceRange?.maxVariantPrice?.amount || null) / 100;

  const priceSourceMapping: {
    [key in OriginalPriceSource]: number;
  } = {
    [OriginalPriceSource.CompareAtPrice]: compareAtPrice,
    [OriginalPriceSource.RegularPrice]: regularPrice,
  };

  const priceList = compact(
    originalPriceSources.map((source: OriginalPriceSource) => priceSourceMapping[source])
  );

  return priceList.length > 0 ? Math.max(...priceList) : maxPrice;
};

const formatContextualPricing = (
  value: { amount: string; currencyCode: Currency } | undefined | null
) =>
  // Number(null) returns 0, but Number(undefined) returns NaN Price on shopify
  value && { amount: Number(value.amount || null), currencyCode: value.currencyCode };

export const getOriginalShopifyProductPrice = (
  brandCurrency: Currency, // Only used to format legacy product price.
  shopifyProduct?: Pick<Product, 'contextualPricing' | 'priceRange'>,
  shopifyProductVariant?: Pick<
    ShopifyProductVariant,
    'contextualPricing' | 'compareAtPrice' | 'price'
  > | null,
  originalPriceSources: OriginalPriceSource[] | null = [
    OriginalPriceSource.CompareAtPrice,
    OriginalPriceSource.RegularPrice,
  ]
): { amount: number; currencyCode?: Currency } | undefined | null => {
  // Default param value will handle undefined values, but need to explicitly handle null values
  // If it is null, that means nothing is selected in Contentful
  const originalPriceSourcesToUse = originalPriceSources ?? [];

  if (shopifyProduct?.contextualPricing || shopifyProductVariant?.contextualPricing) {
    const compareAtPrice = formatContextualPricing(
      shopifyProductVariant?.contextualPricing?.compareAtPrice
    );
    const regularPrice = formatContextualPricing(shopifyProductVariant?.contextualPricing?.price);
    const maxPrice = formatContextualPricing(
      shopifyProduct?.contextualPricing?.priceRange?.maxVariantPrice
    );

    const priceSourceMapping: {
      [key in OriginalPriceSource]: { amount: number; currencyCode?: Currency } | undefined | null;
    } = {
      [OriginalPriceSource.CompareAtPrice]: compareAtPrice,
      [OriginalPriceSource.RegularPrice]: regularPrice,
    };

    const priceList = originalPriceSourcesToUse.map(
      (source: OriginalPriceSource) => priceSourceMapping[source]
    );

    const compactPrices = compact(priceList) as { amount: number; currencyCode?: Currency }[];

    return compactPrices.length > 0
      ? compactPrices.reduce((maxValue, value) =>
          (maxValue?.amount || 0) > value.amount ? maxValue : value
        )
      : maxPrice;
  }

  const legacyPrice = getLegacyOriginalShopifyProductPrice(
    originalPriceSourcesToUse,
    shopifyProduct,
    shopifyProductVariant
  );
  return legacyPrice ? { amount: legacyPrice, currencyCode: brandCurrency } : undefined;
};

export const getShopifyProductVariantCompareAtAndPriceAverage = (
  shopifyProductVariant?: Pick<
    ShopifyProductVariant,
    'contextualPricing' | 'compareAtPrice' | 'price'
  > | null
): number | undefined => {
  const legacyPrice = shopifyProductVariant?.price;
  const legacyCompareAtPrice = shopifyProductVariant?.compareAtPrice;

  const contextualPrice = shopifyProductVariant?.contextualPricing?.price?.amount
    ? Number(shopifyProductVariant.contextualPricing.price.amount)
    : undefined;
  const contextualCompareAtPrice = shopifyProductVariant?.contextualPricing?.compareAtPrice?.amount
    ? Number(shopifyProductVariant.contextualPricing.compareAtPrice.amount)
    : undefined;

  if (contextualPrice && contextualCompareAtPrice) {
    return Number(((contextualPrice + contextualCompareAtPrice) / 2).toFixed(2));
  }

  if (legacyPrice && legacyCompareAtPrice) {
    return Number(((legacyPrice + legacyCompareAtPrice) / 2).toFixed(2));
  }

  return undefined;
};

export const shouldAutoApproveListing = (listing: STListingType) =>
  listing.attributes.publicData.listingItemType === ListingItemType.TradeIn ||
  (listing.attributes.publicData.isBrandDirect &&
    !listing.attributes.publicData.disableAutoApproval);

export const calculateListingPayoutUpdateFields = (
  keyToMatch: string,
  listing: STListingType,
  tradeInCreditPayoutOptions: ITradeInCreditPayoutOption[]
) => {
  const { originalPrice } = listing.attributes.publicData;

  const payoutOption =
    tradeInCreditPayoutOptions.find((option) => option.uniqueKeyOverride === keyToMatch) ||
    tradeInCreditPayoutOptions.find((option) => option.key === keyToMatch);

  if (!payoutOption) {
    throw new Error(
      `Item's key to match (${keyToMatch}) did not map to corresponding uniqueKeyOverride`
    );
  }

  const percentOfOriginalPrice =
    !isNil(payoutOption?.payoutPercentageOfOriginalPrice) && !isNil(originalPrice)
      ? // round to nearest cent
        Math.round(originalPrice * 100 * payoutOption!.payoutPercentageOfOriginalPrice)
      : undefined;

  return {
    listingId: listing.id.uuid,
    fullCreditAmount: listing.attributes.price.amount,
    creditPayoutPercentage: payoutOption?.payoutPercentage,
    creditPayoutFixedAmount: !isNil(payoutOption?.payoutFixedAmount)
      ? payoutOption!.payoutFixedAmount
      : percentOfOriginalPrice,
    tradeInCreditPayoutBundleAmount:
      listing.attributes.publicData?.payoutInfo?.tradeInCreditPayoutBundleAmount,
  };
};

export const calculateListingPayoutUpdateFieldsPG = (
  keyToMatch: string,
  listing: PGListingType,
  tradeInCreditPayoutOptions: ITradeInCreditPayoutOption[]
) => {
  const { originalPrice } = listing;

  const payoutOption =
    tradeInCreditPayoutOptions.find((option) => option.uniqueKeyOverride === keyToMatch) ||
    tradeInCreditPayoutOptions.find((option) => option.key === keyToMatch);

  if (!payoutOption) {
    throw new Error(
      `Item's key to match (${keyToMatch}) did not map to corresponding uniqueKeyOverride`
    );
  }

  const percentOfOriginalPrice =
    !isNil(payoutOption?.payoutPercentageOfOriginalPrice) && !isNil(originalPrice)
      ? // round to nearest cent
        Math.round(originalPrice * payoutOption!.payoutPercentageOfOriginalPrice)
      : undefined;

  return {
    listingId: listing.sharetribeListingId,
    fullCreditAmount: listing.price,
    creditPayoutPercentage: payoutOption?.payoutPercentage,
    creditPayoutFixedAmount: !isNil(payoutOption?.payoutFixedAmount)
      ? payoutOption!.payoutFixedAmount
      : percentOfOriginalPrice,
    tradeInCreditPayoutBundleAmount: (listing.payoutInfo as any)?.tradeInCreditPayoutBundleAmount,
  };
};

export const mapAuthorIdToListingIds = (
  listings: ListingWithAuthorAndImages[]
): { [id: string]: string[] } =>
  listings.reduce((acc: { [id: string]: string[] }, listing: ListingWithAuthorAndImages) => {
    const authorId = listing.author.id.uuid;
    const listingId = listing.id.uuid;
    if (authorId in acc) {
      acc[authorId].push(listingId);
    } else {
      acc[authorId] = [listingId];
    }
    return acc;
  }, {});

export const mapAuthorIdToFulfillmentMethod = (
  authorIdToListingIds: { [id: string]: string[] },
  listings: ListingWithAuthorAndImages[],
  defaultBrandFulfillmentMethod: FulfillmentMethod
): { [id: string]: FulfillmentMethod } =>
  Object.keys(authorIdToListingIds).reduce((acc: { [id: string]: FulfillmentMethod }, authorId) => {
    const listingIds = authorIdToListingIds[authorId];
    const authorListings = listings.filter((listing) => listingIds.includes(listing.id.uuid));
    acc[authorId] = getFulfillmentMethod(authorListings, defaultBrandFulfillmentMethod);
    return acc;
  }, {});

const convertSTListingConditionToPGListingCondition = (
  stCondition: string
): ListingCondition | undefined => {
  const convertedCondition = ST_CONDITION_TO_LISTING_CONDITION[stCondition] as
    | ListingCondition
    | undefined;
  if (!convertedCondition) {
    log.error(
      new Error('Condition not found in ST_CONDITION_TO_LISTING_CONDITION'),
      'convert-ST-listing-condition-to-PG-listing-condition',
      {
        stCondition,
        convertedCondition,
      }
    );
  }
  return convertedCondition;
};

export const convertSTListingToPGListing = (
  stListing: STListingType & { currentStock?: Record<string, any> }
): Partial<PGListingType> => {
  const attributes = stListing?.attributes ?? stListing;
  const { publicData } = attributes;

  const pgListing = {
    createdAt: attributes.createdAt,
    ...(publicData.updatedAt && { updatedAt: new Date(publicData.updatedAt) }),
    sharetribeListingId: stListing.id?.uuid,
    title: attributes.title,
    price: attributes.price?.amount,
    /// Sourced from Shopify Product Variant
    externalSku: publicData.externalSku || publicData.shopifyProductVariantSku,
    availableInventory: stListing?.currentStock?.attributes?.quantity,
    ...(attributes.state && { state: ST_LISTING_STATE_TO_LISTING_STATUS[attributes.state] }),
    ...((publicData.conditionInfo?.condition || publicData.condition) && {
      condition: convertSTListingConditionToPGListingCondition(
        publicData.conditionInfo?.condition ?? publicData.condition
      ),
    }),
    currency: attributes.price?.currency,
    findItemMethod: ST_FIND_ITEM_METHOD_TO_LISTING_FIND_ITEM_METHOD[publicData.findItemMethod],
    shipFromCountry: publicData.shipFromCountry,
    itemBrand: publicData.itemBrand,
    isBrandDirect: publicData.isBrandDirect,
    itemType: publicData.listingItemType,
    sharetribePublicData: publicData,
    ...(publicData.deletedAt && { deletedAt: new Date(publicData.deletedAt) }),
    categories: publicData.categories?.filter((value: string) => !isNil(value)),
    creditBoostPromoId: publicData.payoutCreditBoostPromoId || publicData.creditBoostPromoId,
    ...(!isUndefined(publicData.internalNotes) && {
      approvalMetadata: {
        internalNotes: !isNil(publicData.internalNotes)
          ? publicData.internalNotes.toString()
          : null,
      },
    }),
    ...(!isUndefined(publicData.internalBrandNotes) && {
      privateSellerNotes: !isNil(publicData.internalBrandNotes)
        ? publicData.internalBrandNotes.toString()
        : null,
    }),
    ...(!isUndefined(publicData.conditionInfo?.notes) && {
      notes: !isNil(publicData.conditionInfo?.notes) ? publicData.conditionInfo.notes : null,
    }),
    // Number.isFinite(value) avoids implicit type coercion
    originalPrice: !isNil(publicData.originalPrice)
      ? Math.round(publicData.originalPrice * 100)
      : null,
    payoutInfo: publicData.payoutInfo,
    // set showProductDescription to true to save the product description, whether or not we show it on the listing page
    productHtml: !isNil(publicData.productHtml)
      ? publicData.productHtml
      : getProductDescription(attributes, true, publicData.domain),
    ...(!isUndefined(publicData.conditionInfo?.quirks) && {
      quirks: Array.isArray(publicData.conditionInfo?.quirks)
        ? publicData.conditionInfo?.quirks
        : [],
    }),
    selectedVariantOptions: publicData.selectedVariantOptions,
    tags: publicData.tags,
    stOnlyPublicData: publicData.stOnlyPublicData,
    ...(publicData.approvedAt && { approvedAt: new Date(publicData.approvedAt) }),
    ...(publicData.soldAt && { lastSoldAt: new Date(publicData.soldAt) }),
    promosActive: Array.isArray(publicData?.promosActive)
      ? (publicData.promosActive as Array<string | { uniquePromoId?: unknown }>)
          .map((promo) => {
            if (typeof promo === 'string') return promo;
            if (promo && typeof promo === 'object' && 'uniquePromoId' in promo) {
              return String(promo.uniquePromoId);
            }
            return undefined;
          })
          .filter((id): id is string => !!id)
      : [],
    isReturnable: publicData.isReturnable,
    syncShopifyInventory: publicData.syncShopifyInventory,
    stockPhotoUrls: publicData.stockPhotoUrls,
    images: publicData.images,
    weight: publicData.weight,
    weightUnit: publicData.weightUnit,
    ...(publicData.apiMetadata && {
      apiMetadata: publicData.apiMetadata,
    }),
  };

  return {
    ...Object.fromEntries(Object.entries(pgListing).filter(([, value]) => !isUndefined(value))),
  };
};

const checkForCurrencyForPriceUpdates = (
  pgListing: Partial<PGListingType>
): Currency | undefined => {
  const { price, originalPrice, currency } = pgListing;
  if (!isNil(price) || !isNil(originalPrice)) {
    if (!currency) {
      log.error(
        new Error('No Currency where Currency was expected'),
        'convert-PG-listing-to-ST-listing-no-currency',
        pgListing
      );
    }
    return currency as Currency;
  }
  return undefined;
};

const truncateToTwoDecimals = (value: number): number => Number(value.toFixed(2));

const convertPGListingConditionToSTListingCondition = (
  pgCondition: ListingCondition
): string | null => {
  const convertedCondition = LISTING_CONDITION_TO_ST_CONDITION[pgCondition];
  if (!convertedCondition) {
    log.error(
      new Error('Condition not found in LISTING_CONDITION_TO_ST_CONDITION'),
      'convert-PG-listing-condition-to-ST-listing-condition',
      {
        pgCondition,
        convertedCondition,
      }
    );
  }
  return convertedCondition;
};

export const convertPGListingToSTListing = (
  pgListing: Partial<PGListingType>
): {
  id: Uuid;
  type: 'listing';
  attributes: Partial<ListingAttributes>;
} => {
  // if we don't have a full pgListing.sharetribePublicData reference, then
  // the pgListing update can be assumed to be the publicData update
  const {
    creditBoostPromoId,
    lastSoldAt,
    price,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    currency,
    approvalMetadata,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    priceDropConfig,
    ...publicDataUpdate
  } = pgListing;
  const publicData = pgListing.sharetribePublicData || publicDataUpdate;
  const currencyToUse = checkForCurrencyForPriceUpdates(pgListing);

  // TODO: notes and quirks are not mapped, still updated using stOnlyPublicData
  const publicDataDifferentNames = {
    ...(pgListing.lastSoldAt && { soldAt: lastSoldAt }),
    ...(creditBoostPromoId && { payoutCreditBoostPromoId: creditBoostPromoId }),
    // TODO: condition on conditionInfo is not updated, still need to use stOnlyPublicData
    ...(pgListing?.condition && {
      condition: convertPGListingConditionToSTListingCondition(pgListing.condition),
    }),
    ...(pgListing.findItemMethod && {
      findItemMethod: LISTING_FIND_ITEM_METHOD_TO_ST_FIND_ITEM_METHOD[pgListing?.findItemMethod],
    }),

    ...(approvalMetadata?.internalNotes && {
      internalNotes: approvalMetadata.internalNotes,
    }),
    ...(pgListing.privateSellerNotes && {
      internalBrandNotes: pgListing.privateSellerNotes,
    }),
    // ST original price is still not in sub-units
    ...(!isNil(pgListing.originalPrice) &&
      currencyToUse && {
        originalPrice: truncateToTwoDecimals(pgListing.originalPrice / unitDivisor(currencyToUse)),
      }),
    ...(pgListing.priceDropConfig &&
      currencyToUse && {
        automaticPriceDropInfo: {
          enabled: pgListing.priceDropConfig.isActive,
          percentageDrop: pgListing.priceDropConfig.percentageDrop,
          minPrice: truncateToTwoDecimals(
            pgListing.priceDropConfig.minPrice / unitDivisor(currencyToUse)
          ),
          anchorPrice: truncateToTwoDecimals(
            pgListing.priceDropConfig.anchorPrice / unitDivisor(currencyToUse)
          ),
        },
      }),
  };

  let updatedPrice: Money | undefined;
  if (!isNil(price) && currencyToUse) {
    updatedPrice = {
      amount: price,
      currency: currencyToUse,
    };
  }

  return {
    id: { uuid: pgListing.sharetribeListingId ?? '' },
    attributes: {
      title: pgListing.title,
      description: pgListing.sharetribePublicData?.shopName,
      publicData: { ...publicData, ...publicDataDifferentNames },
      geolocation: null,
      price: updatedPrice,
      createdAt: pgListing.createdAt,
      availabilityPlan: null,
      deleted: false,
      ...(pgListing.state && {
        state: findKey(ST_LISTING_STATE_TO_LISTING_STATUS, (v) => v === pgListing.state),
      }),
    },
    type: 'listing',
  };
};

interface GetSizeVariantValueParams {
  allSizeVariantOptionNames?: string[];
  sizeVariantOptionName: string;
  variantOptions?: { [key: string]: string };
}

export const getSizeVariantValue = ({
  allSizeVariantOptionNames,
  sizeVariantOptionName,
  variantOptions,
}: GetSizeVariantValueParams): string | undefined => {
  if (!allSizeVariantOptionNames || !variantOptions) return undefined;

  if (variantOptions?.[sizeVariantOptionName]) {
    return variantOptions[sizeVariantOptionName];
  }

  const foundSizeVariant = allSizeVariantOptionNames.find(
    (sizeOptionName) => variantOptions?.[sizeOptionName]
  );

  return foundSizeVariant ? variantOptions[foundSizeVariant] : undefined;
};
